import { Fragment, useEffect, useMemo, useState } from 'react'

import { useAssets } from 'core/logic/asset/asset.hook'
import { useParams } from 'react-router-dom'
import { useUser } from 'core/logic/user'
import BuyOfferView from './BuyOffer.view'
import {
  useMarketplaceInterface,
  useMarketplaceSale,
  useSaleAvailableOnMarketplace,
} from 'context/Marketplace'
import { useMarketplaceUser } from 'context/MarketplaceUser'
import WalletRequiredWrapper from 'components/WalletRequiredWrapper'
import ROUTES, {
  TO_COLLECTION_ITEM_DETAIL,
  TO_DROP_PENDING_PAYMENT,
} from 'core/modules/router'
import useCustomHistory from '@onepercentio/one-ui/hooks/useCustomHistory'
import { useGallery } from 'context/Gallery'
import { ContractType } from 'core/logic/contract/types/types'
import DigitalTwinLogic from './OfferType/DigitalTwin'
import useCheckUndefined from 'hooks/useCheckUndefined'
import { CustodyZeroXMarketplace } from 'service/IMarketplace/ZeroXMarketplace'
import OverlayKYCWrapperLogic from 'openspace/components/OverlayKYCWrapper'
import { DeliveryQuotation } from '@onepercentio/rarum-types/logistics'
import MultiStepApprovalLogic from 'features/MultiStepApproval'
import { t } from 'translate/i18n'
import { UserAddress } from 'core/modules/firebase/service/user'
import Styles from './BuyOffer.module.scss'
import BigNumber from 'bignumber.js'

function BuyOfferLogic() {
  const { assetId, offerId } = useParams<{ assetId: string; offerId: string }>()
  const { assets } = useAssets()
  const history = useCustomHistory()
  const assetToShow = assets?.find((a) => a.id === assetId)

  const user = useUser()
  const completeUser =
    user.detail && user.profile
      ? { ...user.detail, ...user.profile }
      : undefined

  const thisSale = useSaleAvailableOnMarketplace(offerId)
  const gallery = useGallery(assetToShow?.galleryId!)
  const [allowance, setAllowance] = useState<number>()

  const {
    balance,
    buyOffer,
    control: buyOfferControl,
    updateBalance,
    getTokensAllowance,
    approveTokenAllowance,
  } = useMarketplaceUser()
  const userBalance = useMemo(() => {
    return thisSale ? balance[thisSale.currency] : undefined
  }, [balance, thisSale])

  const [bought, setBought] = useState(false)
  const [address, setAddress] = useState<UserAddress>()
  const [quote, setQuote] = useState<DeliveryQuotation>()

  useEffect(() => {
    updateBalance()
  }, [])
  useEffect(() => {
    if (thisSale)
      getTokensAllowance(thisSale.currency, thisSale.price).then(setAllowance)
  }, [thisSale])

  const needsBalanceApproval =
    allowance === undefined ? undefined : !(allowance > 0)

  function routeToItem() {
    history.replace(TO_COLLECTION_ITEM_DETAIL(assetId, assetToShow!.tokenId))
  }

  const _cancel = () =>
    history.goBackWithFallback(ROUTES.unauthenticated.homepage)

  const _approveAllowance = () => {
    const priceToApprove = BigNumber(thisSale!.price!)
      .plus(thisSale?.quote?.price ? thisSale.quote.price : 0)
      .toNumber()
    approveTokenAllowance(thisSale!.currency!, priceToApprove).then(() =>
      getTokensAllowance(thisSale!.currency, thisSale!.price!).then(
        setAllowance
      )
    )
  }

  const confirmCallback = !buyOfferControl.loading
    ? async () => {
        if (address) await user.updateLastAddress(address)
        if (!bought) {
          return await buyOffer({
            ...thisSale!,
            address,
            quote,
          }).then((paymentId) => {
            setBought(true)
            if (paymentId) history.push(TO_DROP_PENDING_PAYMENT(paymentId))
          })
        } else return Promise.resolve(routeToItem())
      }
    : () => Promise.resolve()

  const _retry = () => {
    if (!userBalance) updateBalance()
    else if (!allowance)
      getTokensAllowance(thisSale!.currency, thisSale!.price!).then(
        setAllowance
      )
    else if (needsBalanceApproval) _approveAllowance()
    else confirmCallback().then(() => setBought(true))
  }

  useEffect(() => {
    updateBalance()
  }, [updateBalance])

  const [isSomethingUndefined] = useCheckUndefined({
    gallery,
    assetToShow,
    thisSale,
  })

  const [showConfirmationFlow, setShowConfirmationFlow] = useState(false)

  if (isSomethingUndefined) return null
  const content = (() => {
    if (gallery!.smartContract?.type === ContractType.DigitalTwin)
      if (showConfirmationFlow)
        return (
          <div className={Styles.container}>
            <MultiStepApprovalLogic
              onCancel={() => setShowConfirmationFlow(false)}
              onTargetAction={confirmCallback}
              ManualComponent={() => {
                throw new Error(
                  `The manual flow for digital twin should not be called`
                )
              }}
              label={{
                loading: 'offer.buy.loading',
              }}
              mode='erc20'
              currency={thisSale?.currency!}
              price={thisSale?.price!}
            />
          </div>
        )
      else
        return (
          <DigitalTwinLogic
            asset={assetToShow!}
            sale={thisSale!}
            onBuyOffer={(add, qu) => {
              setAddress(add)
              setQuote(qu)
              setShowConfirmationFlow(true)
            }}
          />
        )
    return (
      <BuyOfferView
        key={'buy_result'}
        error={!!buyOfferControl.error}
        onRetry={_retry}
        asset={assetToShow}
        user={completeUser}
        sale={thisSale}
        userBalance={userBalance}
        onConfirm={confirmCallback}
        onCancel={_cancel}
        onApproveAllowance={_approveAllowance}
        loading={buyOfferControl.loading}
        bought={!!bought}
        needsBalanceApproval={needsBalanceApproval}
      />
    )
  })()

  const KYCWrapper = gallery?.marketplace?.requirements
    ? OverlayKYCWrapperLogic
    : Fragment

  return (
    <KYCWrapper
      prereqTo='marketplace-buy'
      requirement={gallery?.marketplace}
      onClose={history.goBack}>
      {content}
    </KYCWrapper>
  )
}

export default function BuyOfferLogicWrapper() {
  const marketplace = useMarketplaceInterface()
  const doesThisMarketplaceIntegrationAllowCustody = useMemo(() => {
    if (marketplace instanceof CustodyZeroXMarketplace) return true
    else return false
  }, [marketplace])

  return (
    <WalletRequiredWrapper
      allowCustody={doesThisMarketplaceIntegrationAllowCustody}
      requiresMigration={!doesThisMarketplaceIntegrationAllowCustody}>
      <BuyOfferLogic />
    </WalletRequiredWrapper>
  )
}
